import { Link } from "react-router";
import { useAuth } from "@/app/auth-context";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

interface CardItem { to: string; label: string; perm?: string }
const groups: { title: string; items: CardItem[] }[] = [
  { title: "Clients & Accounts", items: [
    { to: "/clients",  label: "Clients",  perm: "clients:read" },
    { to: "/accounts", label: "Accounts", perm: "accounts:read" },
    { to: "/invoices", label: "Invoices", perm: "invoices:read" },
    { to: "/payments", label: "Payments", perm: "payments:read" },
    { to: "/finance/reconciled-today", label: "Reconciled Today", perm: "reconciliation:read" },
    { to: "/finance/transactions",     label: "Transactions",     perm: "transactions:read" },
    // Plan III Task 21 — route added in Tasks 22/23/24
    { to: "/statements/runs", label: "Statement runs", perm: "statements:read" },
  ]},
  { title: "People", items: [
    { to: "/admin/employees", label: "Employees", perm: "employees:read" },
    { to: "/me",              label: "My profile" },
  ]},
  { title: "Org", items: [
    { to: "/admin/branches",     label: "Branches",     perm: "branches:read" },
    { to: "/admin/branch-types", label: "Branch types", perm: "branches:read" },
    { to: "/admin/companies",    label: "Companies",    perm: "branches:read" },
  ]},
  { title: "System", items: [
    { to: "/admin/system-parameters", label: "System parameters", perm: "system-params:read" },
    { to: "/admin/next-numbers",      label: "Next numbers",      perm: "next-number:read" },
    { to: "/admin/file-mappings",     label: "File mappings",     perm: "lookups:read" },
  ]},
  { title: "Quotes", items: [
    { to: "/admin/quote-templates", label: "Quote templates",  perm: "quote-templates:read" },
    { to: "/admin/quote-tandcs",    label: "Quote T&Cs",       perm: "quote-tandcs:read" },
    { to: "/admin/approval-types",  label: "Approval types",   perm: "approval-types:read" },
    { to: "/admin/job-status-codes", label: "Job status codes", perm: "job-status-codes:read" },
    { to: "/admin/invoice-statuses", label: "Invoice statuses", perm: "invoice-lookups:read" },
    { to: "/admin/account-terms",   label: "Account terms",    perm: "accounts:read" },
  ]},
  { title: "Lookups", items: [
    { to: "/admin/printers",                label: "Printers",                       perm: "printers:read" },
    { to: "/admin/status-groups",           label: "Status groups",                  perm: "lookups:read" },
    { to: "/admin/issued-link-types",       label: "Issued link types",              perm: "lookups:read" },
    { to: "/admin/notification-types",      label: "Notification types",             perm: "lookups:read" },
    { to: "/admin/user-notification-types", label: "User notification types",        perm: "lookups:read" },
    { to: "/admin/delivery-methods",        label: "Delivery methods",               perm: "lookups:read" },
    { to: "/admin/stmt-delivery-methods",   label: "Statement delivery",             perm: "lookups:read" },
    { to: "/admin/transaction-types",       label: "Transaction types",              perm: "lookups:read" },
    { to: "/admin/from-email-types",        label: "From-email types",               perm: "lookups:read" },
    { to: "/admin/payment-methods",          label: "Payment methods",                perm: "payment-lookups:read" },
    { to: "/admin/payment-types",            label: "Payment types",                  perm: "payment-lookups:read" },
    { to: "/admin/payment-gateways",         label: "Payment gateways",               perm: "payment-lookups:read" },
  ]},
  { title: "Operations", items: [
    { to: "/admin/jobs-launcher", label: "Hangfire dashboard" },
    { to: "/admin/user-audit",   label: "User audit log",   perm: "user-audit:read" },
    { to: "/admin/system-links", label: "System links",     perm: "lookups:read" },
  ]},
];

export function AdminLandingPage() {
  const { me, permissions } = useAuth();
  const isAdmin = me?.roles.includes("Admin") ?? false;
  const mfaPending = isAdmin && me?.mfaEnabled === false;
  return (
    <div className="space-y-6 p-6">
      <h1 className="text-3xl font-semibold">Administration</h1>
      {mfaPending && (
        <Badge variant="destructive">MFA enrolment required — visit My Profile</Badge>
      )}
      {groups.map((g) => {
        const visible = g.items.filter((i) => !i.perm || permissions.has(i.perm));
        if (visible.length === 0) return null;
        return (
          <section key={g.title} aria-labelledby={`grp-${g.title}`} className="space-y-3">
            <h2 id={`grp-${g.title}`} className="text-xl font-medium">{g.title}</h2>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {visible.map((i) => (
                <Card key={i.to}>
                  <CardHeader><CardTitle>{i.label}</CardTitle></CardHeader>
                  <CardContent><Link to={i.to} className="text-sm text-primary underline">Open</Link></CardContent>
                </Card>
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}
